import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  CircularProgress,
  Alert,
  Divider,
} from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { ShoppingCart } from "@mui/icons-material";
import { Attendee, Price } from "../../redux/types/types";
import { RootState } from "../../redux/store";
import {
  getSpecialClassAccessForPayment,
  shouldShowClassCounters,
} from "../../utils/specialClassAccess";
import {
  createStripePaymentApi,
  Price as ApiPrice,
  getSuitablePricesForAttendeeApi,
} from "../../utils/api/payment";
import { sortPricesForDisplay } from "../../utils/priceUtils";
import { MultiPackagePaymentDialog } from "./MultiPackagePaymentDialog";
import { useMultiPackagePayment } from "../../hooks/useMultiPackagePayment";
import Notiflix from "notiflix";
import AttendeeImportantInfoButton from "../Attendees/AttendeeImportantInfoButton";

interface PaymentDialogProps {
  open: boolean;
  onClose: () => void;
  attendee: Attendee;
  onPaymentCreated?: () => void;
  classIds?: number[]; // Optional - if provided, filters prices for specific classes
}

export default function PaymentDialog({
  open,
  onClose,
  attendee,
  onPaymentCreated,
  classIds,
}: PaymentDialogProps) {
  const intl = useIntl();
  const isAdmin = useSelector((state: RootState) => state.auth.isAdmin);
  const isInstructor = useSelector(
    (state: RootState) => state.auth.isInstructor,
  );
  const userClasses = useSelector(
    (state: RootState) => state.auth.user.classes || [],
  );
  const adminClasses = useSelector(
    (state: RootState) => state.admin.classes || [],
  );
  const [prices, setPrices] = useState<Price[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedPrice, setSelectedPrice] = useState<Price | null>(null);
  const [creating, setCreating] = useState(false);
  const [multiPackageDialogOpen, setMultiPackageDialogOpen] = useState(false);

  // Only admins and instructors can do manual payments
  const canDoManualPayment = Boolean(isAdmin || isInstructor);

  const {
    loading: multiPackageLoading,
    handleManualPayment,
    handleStripePayment,
  } = useMultiPackagePayment();

  const loadPrices = useCallback(async () => {
    setLoading(true);
    try {
      let response;
      if (classIds && classIds.length > 0) {
        // For attendees with enrolled classes, get suitable prices
        response = await getSuitablePricesForAttendeeApi({
          attendeeId: attendee.id,
          enrolledClassIds: classIds,
        });
      } else {
        // For general payment (no specific enrollments), use all prices
        response = { data: [] };
      }
      // Convert API prices to Redux Price types
      const convertedPrices: Price[] = response.data.map(
        (apiPrice: ApiPrice) => ({
          id: apiPrice.id,
          numberOfClasses: apiPrice.numberOfClasses,
          specialType: apiPrice.specialType,
          amount: apiPrice.amount,
          currency: apiPrice.currency,
          isAdminOnly: apiPrice.isAdminOnly || false,
          createdAt: new Date(apiPrice.createdAt),
          updatedAt: new Date(apiPrice.updatedAt),
        }),
      );

      // Sort prices using utility function
      const sortedPrices = sortPricesForDisplay(convertedPrices);

      setPrices(sortedPrices);
    } catch (error) {
      console.error("Failed to load prices:", error);
      Notiflix.Notify.failure(
        intl.formatMessage({ id: "payment.loadPricesError" }),
      );
    } finally {
      setLoading(false);
    }
  }, [intl, classIds, attendee.id]);

  useEffect(() => {
    if (open) {
      loadPrices();
    }
  }, [open, loadPrices]);

  const handlePriceSelect = (price: Price) => {
    setSelectedPrice(price);
  };

  const handleCreatePayment = async () => {
    if (!selectedPrice) return;

    setCreating(true);
    try {
      const response = await createStripePaymentApi({
        attendeeId: attendee.id,
        priceId: selectedPrice.id,
      });

      // Safari-compatible redirect - use window.location.href for better compatibility
      window.location.href = response.data.paymentUrl;

      Notiflix.Notify.success(
        intl.formatMessage({ id: "payment.redirectToStripe" }),
      );

      onClose();
      if (onPaymentCreated) {
        onPaymentCreated();
      }
    } catch (error) {
      console.error("Failed to create payment:", error);
      Notiflix.Notify.failure(
        intl.formatMessage({ id: "payment.createError" }),
      );
    } finally {
      setCreating(false);
    }
  };

  const formatPrice = (amount: number) => {
    return (amount / 100).toFixed(2);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <FormattedMessage
          id="payment.title"
          defaultMessage="Purchase Classes"
        />
        {` - ${attendee.firstName} ${attendee.lastName}`}
      </DialogTitle>
      <DialogContent>
        <AttendeeImportantInfoButton attendee={attendee} />
        <Box mb={2}>
          {shouldShowClassCounters(
            attendee,
            classIds,
            isAdmin ? adminClasses : userClasses,
          ) && (
            <Typography variant="body1" color="text.secondary">
              <FormattedMessage
                id="payment.currentClasses"
                defaultMessage="Current remaining classes: {count}"
                values={{ count: attendee.remainingClasses }}
              />
            </Typography>
          )}
          {/* Show special class access information for all attendees */}
          <Box mt={1}>
            {getSpecialClassAccessForPayment(
              attendee,
              isAdmin ? adminClasses : userClasses,
              intl.formatMessage,
            ).map((accessInfo, index) => (
              <Typography key={index} variant="body2" color="text.secondary">
                {accessInfo}
              </Typography>
            ))}
          </Box>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              <FormattedMessage
                id="payment.selectPackage"
                defaultMessage="Select a Package"
              />
            </Typography>

            <Grid container spacing={2}>
              {prices.map((price) => (
                <Grid item xs={12} sm={6} md={4} key={price.id}>
                  <Card
                    sx={{
                      cursor: "pointer",
                      border: selectedPrice?.id === price.id ? 2 : 1,
                      borderColor:
                        selectedPrice?.id === price.id
                          ? "primary.main"
                          : "divider",
                      "&:hover": {
                        boxShadow: 3,
                      },
                    }}
                    onClick={() => handlePriceSelect(price)}
                  >
                    <CardContent>
                      <Typography variant="h6" component="div" gutterBottom>
                        {price.specialType ||
                          intl.formatMessage(
                            { id: "payment.classPackage" },
                            { count: price.numberOfClasses },
                          )}
                      </Typography>
                      <Typography variant="h4" color="primary" gutterBottom>
                        {formatPrice(price.amount)} CHF
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <FormattedMessage
                          id="payment.classesIncluded"
                          defaultMessage="{count} classes included"
                          values={{ count: price.numberOfClasses }}
                        />
                      </Typography>
                      {price.numberOfClasses > 1 && (
                        <Typography variant="body2" color="success.main">
                          <FormattedMessage
                            id="payment.pricePerClass"
                            defaultMessage="{price} CHF per class"
                            values={{
                              price: formatPrice(
                                price.amount / price.numberOfClasses,
                              ),
                            }}
                          />
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {prices.length === 0 && !loading && (
              <Alert severity="info">
                <FormattedMessage
                  id="payment.noPricesAvailable"
                  defaultMessage="No pricing packages available at the moment."
                />
              </Alert>
            )}

            {/* Multi-package Payment Option */}
            {prices.length > 0 && (
              <>
                <Divider sx={{ my: 3 }} />
                <Box textAlign="center">
                  <Typography variant="h6" gutterBottom>
                    {intl.formatMessage({ id: "payment.multiPackage.title" })}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    {intl.formatMessage({
                      id: "payment.multiPackage.description",
                    })}
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<ShoppingCart />}
                    onClick={() => setMultiPackageDialogOpen(true)}
                    disabled={creating || multiPackageLoading}
                  >
                    {intl.formatMessage({
                      id: "payment.multiPackage.openCart",
                    })}
                  </Button>
                </Box>
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={creating}>
          <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
        </Button>
        <Button
          onClick={handleCreatePayment}
          variant="contained"
          disabled={!selectedPrice || creating}
          startIcon={creating ? <CircularProgress size={20} /> : null}
        >
          {creating ? (
            <FormattedMessage
              id="payment.creating"
              defaultMessage="Creating..."
            />
          ) : (
            <FormattedMessage
              id="payment.proceedToPayment"
              defaultMessage="Proceed to Payment"
            />
          )}
        </Button>
      </DialogActions>

      {/* Multi-Package Payment Dialog */}
      <MultiPackagePaymentDialog
        open={multiPackageDialogOpen}
        onClose={() => setMultiPackageDialogOpen(false)}
        attendeeId={attendee.id}
        prices={prices}
        onManualPayment={async (cartItems) => {
          const result = await handleManualPayment(attendee.id, cartItems);
          if (result.success) {
            setMultiPackageDialogOpen(false);
            onClose();
            if (onPaymentCreated) {
              onPaymentCreated();
            }
          }
        }}
        onStripePayment={async (cartItems) => {
          const result = await handleStripePayment(attendee.id, cartItems);
          if (result.success) {
            setMultiPackageDialogOpen(false);
            onClose();
            if (onPaymentCreated) {
              onPaymentCreated();
            }
          }
        }}
        loading={multiPackageLoading}
        canDoManualPayment={canDoManualPayment}
      />
    </Dialog>
  );
}
